/**
 * normalizeProgram.js
 *
 * Safely extracts fields from a Firestore program document regardless of
 * whether it came from the web SDK (_document internals) or the native
 * Capacitor Firestore plugin (plain object).
 *
 * Always returns a flat, JSON-safe object — safe to store in localStorage
 * and read back on any platform without touching _document internals.
 */

export function normalizeProgram(program, index = 0) {
    try {
        // ── Path 1: Web SDK internal shape ──────────────────────────────────────
        // program._document.data.value.mapValue.fields.*
        if (program?._document?.data?.value?.mapValue?.fields) {
            const fields = program._document.data.value.mapValue.fields;
            const segments = program._document.key?.path?.segments || [];
            return {
                id: segments[6] || segments[segments.length - 1] || `program_${index}`,
                Program_name: fields.Program_name?.stringValue || '',
                Template_id: fields.Template_id?.stringValue || '',
                // Preserve the original for any code still using _document directly
                // (transition period — remove once all consumers use normalized shape)
                _raw: program,
            };
        }

        // ── Path 2: Already normalized (came back from localStorage on iOS) ──────
        if (program?.Program_name && program?.Template_id) {
            return {
                id: program.id || `program_${index}`,
                Program_name: program.Program_name,
                Template_id: program.Template_id,
                _raw: program._raw || program,
            };
        }

        // ── Path 3: Native Capacitor plugin shape (plain Firestore doc) ──────────
        // { id, data: { Program_name, Template_id, ... } }
        if (program?.data?.Program_name) {
            return {
                id: program.id || `program_${index}`,
                Program_name: program.data.Program_name || '',
                Template_id: program.data.Template_id || '',
                _raw: program,
            };
        }

        console.warn('normalizeProgram: unrecognised shape at index', index, program);
        return null;
    } catch (err) {
        console.error('normalizeProgram: error normalizing program at index', index, err);
        return null;
    }
}

/**
 * Normalize an array of programs and filter out any that failed.
 */
export function normalizeProgramList(programs = []) {
    return programs
        .map((p, i) => normalizeProgram(p, i))
        .filter(Boolean);
}